import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, useTheme } from '@mui/material';
import { 
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';

// Import form components
import { FormStepperEnhanced, FormStep } from '../../components/shared/forms';
import { FormPageHeader } from '../../components/ui/FormFields';
import { FormSection } from '../../design-system/components/forms';
import { TextField, Select, DatePicker, MagicLoader } from '../../design-system';

// Import stores
import useLeadStore from '../../stores/leadStore';
import useUserStore from '../../stores/userStore';
import useNotificationStore from '../../stores/notificationStore';

// Form configuration
const leadSources = [
  { value: 'website', label: 'Website' },
  { value: 'social_media', label: 'Social Media' },
  { value: 'referral', label: 'Referral' },
  { value: 'email_campaign', label: 'Email Campaign' },
  { value: 'phone', label: 'Phone' },
  { value: 'event', label: 'Event' },
  { value: 'direct', label: 'Direct' },
  { value: 'other', label: 'Other' }
];

const leadStatuses = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal', label: 'Proposal' },
  { value: 'negotiation', label: 'Negotiation' },
  { value: 'won', label: 'Won' },
  { value: 'lost', label: 'Lost' }
];

// Initial lead data
const initialLeadData = {
  // Basic Information
  name: '',
  email: '',
  phone: '',
  status: 'new',
  source: 'website',
  assignedTo: '',
  
  // Company Details
  company: '',
  title: '',
  website: '',
  industry: '',
  
  // Opportunity
  estimatedValue: 0,
  probability: 10,
  expectedCloseDate: '',
  description: ''
};

const LeadForm = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  // Store hooks
  const selectedLead = useLeadStore(state => state.selectedLead);
  const fetchLeadById = useLeadStore(state => state.fetchLeadById);
  const createLead = useLeadStore(state => state.createLead);
  const updateLead = useLeadStore(state => state.updateLead);
  const clearSelectedLead = useLeadStore(state => state.clearSelectedLead);
  const isLoading = useLeadStore(state => state.loading);
  
  const users = useUserStore(state => state.users);
  const fetchUsers = useUserStore(state => state.fetchUsers);
  const { showSuccess, showError } = useNotificationStore();
  
  // Local state
  const [activeStep, setActiveStep] = useState(0);
  const [leadData, setLeadData] = useState(initialLeadData);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Load lead data if editing
  useEffect(() => {
    if (isEditMode) {
      fetchLeadById(id).catch(error => {
        showError('Failed to fetch lead: ' + error.message);
        navigate('/leads');
      });
    }
    
    return () => {
      clearSelectedLead();
    };
  }, [id, isEditMode, fetchLeadById, clearSelectedLead, showError, navigate]);
  
  // Set lead data when selectedLead changes (for edit mode)
  useEffect(() => {
    if (isEditMode && selectedLead) {
      setLeadData({
        name: selectedLead.name || '',
        email: selectedLead.email || '',
        phone: selectedLead.phone || '',
        status: selectedLead.status || 'new',
        source: selectedLead.source || 'website',
        assignedTo: selectedLead.assignedTo?._id || '',
        company: selectedLead.company || '',
        title: selectedLead.title || '',
        website: selectedLead.website || '',
        industry: selectedLead.industry || '',
        estimatedValue: selectedLead.estimatedValue || 0,
        probability: selectedLead.probability || 10,
        expectedCloseDate: selectedLead.expectedCloseDate || '',
        description: selectedLead.description || ''
      });
    }
  }, [isEditMode, selectedLead]);
  
  // Load users for assignment
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);
  
  // Field change handler
  const handleFieldChange = (field, value) => {
    setLeadData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
    
    // Clear error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };
  
  // Get validation rules for each step
  const getValidationRules = (stepIndex) => {
    switch (stepIndex) {
      case 0: // Basic Information
        return {
          name: {
            required: true,
            message: 'Lead name is required'
          },
          email: {
            required: true,
            message: 'Email is required',
            validator: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
            validatorMessage: 'Invalid email format'
          },
          phone: {
            validator: (value) => !value || /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{4,6}$/.test(value),
            validatorMessage: 'Invalid phone number format'
          },
          source: {
            required: true,
            message: 'Lead source is required'
          }
        };
        
      case 1: // Company Details
        return {}; // All fields are optional
        
      case 2: // Opportunity
        return {
          estimatedValue: {
            validator: (value) => value >= 0,
            validatorMessage: 'Value cannot be negative'
          },
          probability: {
            validator: (value) => value >= 0 && value <= 100,
            validatorMessage: 'Probability must be between 0 and 100'
          }
        };
        
      default:
        return {};
    }
  };
  
  // Step validation
  const validateStep = async (stepIndex) => {
    const errors = {};
    let isValid = true;
    
    const validationRules = getValidationRules(stepIndex);
    
    for (const [field, rule] of Object.entries(validationRules)) {
      const value = leadData[field];
      
      // Check required
      if (rule.required && (!value || value === '')) {
        errors[field] = rule.message;
        isValid = false;
      }
      // Check validator
      else if (rule.validator && value && !rule.validator(value)) {
        errors[field] = rule.validatorMessage || rule.message;
        isValid = false;
      }
    }
    
    setValidationErrors(errors);
    return isValid;
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const leadPayload = {
        ...leadData,
        estimatedValue: parseFloat(leadData.estimatedValue) || 0,
        probability: parseInt(leadData.probability) || 10,
        assignedTo: leadData.assignedTo || undefined,
        expectedCloseDate: leadData.expectedCloseDate || undefined
      };
      
      if (isEditMode) {
        await updateLead(id, leadPayload);
        showSuccess('Lead updated successfully');
        navigate(`/leads/${id}`);
      } else {
        const response = await createLead(leadPayload);
        const newLeadId = response.data?.lead?._id;
        showSuccess('Lead created successfully');
        navigate(newLeadId ? `/leads/${newLeadId}` : '/leads');
      }
    } catch (error) {
      showError(isEditMode 
        ? 'Failed to update lead: ' + error.message 
        : 'Failed to create lead: ' + error.message
      );
      setSubmitting(false);
    }
  };
  
  // Loading state
  if ((isLoading && isEditMode && !selectedLead)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <MagicLoader variant="mystical" size="large" message="Loading lead data..." />
      </Box>
    );
  }
  
  // Define steps
  const steps = [
    {
      label: 'Basic Information',
      icon: <PersonIcon />,
      optional: false,
      description: 'Lead contact details',
      component: (
        <FormStep
          label="Basic Information"
          description="Enter the lead's contact details"
          icon={<PersonIcon />}
        >
          <FormSection>
            <TextField
              label="Lead Name"
              value={leadData.name}
              onChange={(e) => handleFieldChange('name', e.target.value)}
              required
              error={!!validationErrors.name}
              helperText={validationErrors.name}
              placeholder="Enter lead name"
            />
            
            <TextField
              label="Email Address"
              type="email"
              value={leadData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              required
              error={!!validationErrors.email}
              helperText={validationErrors.email}
              placeholder="email@example.com"
            />
            
            <TextField
              label="Phone Number"
              value={leadData.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              error={!!validationErrors.phone}
              helperText={validationErrors.phone}
              placeholder="+1 (555) 123-4567"
            />
            
            <Select
              label="Lead Status"
              value={leadData.status}
              onChange={(e) => handleFieldChange('status', e.target.value)}
              options={leadStatuses}
              required
            />
            
            <Select
              label="Lead Source"
              value={leadData.source}
              onChange={(e) => handleFieldChange('source', e.target.value)}
              options={leadSources}
              required
              error={!!validationErrors.source}
              helperText={validationErrors.source}
            />
            
            <Select
              label="Assign To"
              value={leadData.assignedTo}
              onChange={(e) => handleFieldChange('assignedTo', e.target.value)}
              options={[
                { value: '', label: 'Unassigned' },
                ...users.map(user => ({
                  value: user._id,
                  label: `${user.firstName} ${user.lastName}`
                }))
              ]}
            />
          </FormSection>
        </FormStep>
      )
    },
    {
      label: 'Company Details',
      icon: <BusinessIcon />,
      optional: true,
      description: 'Company information',
      component: (
        <FormStep
          label="Company Details"
          description="Enter company and role information"
          icon={<BusinessIcon />}
        >
          <FormSection>
            <TextField
              label="Company Name"
              value={leadData.company}
              onChange={(e) => handleFieldChange('company', e.target.value)}
              placeholder="Enter company name"
            />
            
            <TextField
              label="Job Title"
              value={leadData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="Enter job title"
            />
            
            <TextField
              label="Website"
              value={leadData.website}
              onChange={(e) => handleFieldChange('website', e.target.value)}
              placeholder="https://example.com"
            />
            
            <TextField
              label="Industry"
              value={leadData.industry}
              onChange={(e) => handleFieldChange('industry', e.target.value)}
              placeholder="Enter industry"
            />
          </FormSection>
        </FormStep>
      )
    },
    {
      label: 'Opportunity',
      icon: <MoneyIcon />,
      optional: true,
      description: 'Deal value and probability',
      component: (
        <FormStep
          label="Opportunity Details"
          description="Enter deal value and probability"
          icon={<MoneyIcon />}
        >
          <FormSection>
            <TextField
              label="Estimated Value"
              type="number"
              value={leadData.estimatedValue}
              onChange={(e) => handleFieldChange('estimatedValue', parseFloat(e.target.value) || 0)}
              error={!!validationErrors.estimatedValue}
              helperText={validationErrors.estimatedValue}
              startAdornment="$"
            />
            
            <TextField
              label="Probability (%)"
              type="number"
              value={leadData.probability}
              onChange={(e) => handleFieldChange('probability', parseInt(e.target.value) || 0)}
              error={!!validationErrors.probability}
              helperText={validationErrors.probability}
              InputProps={{
                inputProps: { min: 0, max: 100 }
              }}
            />
            
            <DatePicker
              label="Expected Close Date"
              value={leadData.expectedCloseDate}
              onChange={(value) => handleFieldChange('expectedCloseDate', value)}
              minDate={new Date().toISOString().split('T')[0]}
            />
            
            <TextField
              label="Description"
              value={leadData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              multiline
              rows={4}
              placeholder="Enter any additional notes..."
            />
          </FormSection>
        </FormStep>
      )
    }
  ];
  
  // Breadcrumbs
  const breadcrumbs = [
    { label: 'Leads', path: '/leads' },
    { label: isEditMode ? `Edit ${selectedLead?.name || 'Lead'}` : 'Create New Lead' }
  ];
  
  return (
    <>
      <FormPageHeader
        title={isEditMode ? 'Edit Lead' : 'Create Lead'}
        breadcrumbs={breadcrumbs}
        backPath="/leads"
      />
      
      <FormStepperEnhanced
        steps={steps}
        activeStep={activeStep}
        onStepChange={setActiveStep}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/leads')}
        validateStep={validateStep}
        linear={false}
        showCompletionStep={false}
        nextButtonText="Next"
        backButtonText="Back"
        submitButtonText={isEditMode ? 'Update Lead' : 'Create Lead'}
        submitButtonIcon={<SaveIcon />}
        submitting={submitting}
        magical={true}
        formState={leadData}
        setFormState={setLeadData}
        errors={validationErrors}
      />
    </>
  );
};

export default LeadForm;